import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, Info, MoreHorizontal, Pencil, Users } from "lucide-react";
import { useTRPC } from "@/trpc";
import { BillingPlanDialog } from "@/components/billing-plan/billing-plan-dialog";

const DEFAULT_MONTHLY_FREE_PSV_LIMIT = 2;

interface BusinessUnit {
  id: string;
  name: string;
  created_at: string;
  auto_unpublish_job_posts_days: number | null;
  require_provider_reveal: boolean;
  monthly_provider_reveal_limit: number | null;
  enable_hiring_process: boolean;
  enable_document_signing: boolean;
  enable_provider_search: boolean;
  skip_job_post_review: boolean;
  use_new_credential_model: boolean;
  enable_primary_source_verification: boolean;
  monthly_free_primary_source_verification_limit: number | null;
}

type BooleanFeatureFlagKey =
  | "enable_hiring_process"
  | "enable_document_signing"
  | "enable_provider_search"
  | "skip_job_post_review"
  | "use_new_credential_model"
  | "enable_primary_source_verification";

const FEATURE_FLAGS: Array<{ key: BooleanFeatureFlagKey; label: string }> = [
  { key: "enable_hiring_process", label: "Hiring Process" },
  { key: "enable_document_signing", label: "Document Signing" },
  { key: "enable_provider_search", label: "Provider Search" },
  { key: "skip_job_post_review", label: "Skip Review" },
  { key: "use_new_credential_model", label: "New Credentials" },
  {
    key: "enable_primary_source_verification",
    label: "Primary Source Verification",
  },
];

const FEATURE_SETTING_ROW_CLASS_NAME =
  "flex min-w-0 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4";

function SettingLabel({
  htmlFor,
  children,
  description,
}: {
  htmlFor: string;
  children: React.ReactNode;
  description?: string;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <Label htmlFor={htmlFor} className="min-w-0 leading-6">
        {children}
      </Label>
      {description && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex size-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-primary/5 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label={`More information about ${String(children)}`}
            >
              <Info aria-hidden="true" className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            collisionPadding={16}
            className="w-72 max-w-[calc(100vw-2rem)] rounded-xl border-slate-200 bg-white text-sm leading-6 text-slate-600 shadow-lg"
          >
            {description}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

const BusinessUnitsPage = () => {
  const { orgId } = Route.useParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [autoUnpublishEnabled, setAutoUnpublishEnabled] = useState(true);
  const [autoUnpublishDays, setAutoUnpublishDays] = useState(30);
  const [hiringProcessEnabled, setHiringProcessEnabled] = useState(false);
  const [documentSigningEnabled, setDocumentSigningEnabled] = useState(false);
  const [providerSearchEnabled, setProviderSearchEnabled] = useState(false);
  const [skipJobPostReview, setSkipJobPostReview] = useState(false);
  const [useNewCredentialModel, setUseNewCredentialModel] = useState(true);
  const [
    primarySourceVerificationEnabled,
    setPrimarySourceVerificationEnabled,
  ] = useState(false);
  const [freePsvLimitEnabled, setFreePsvLimitEnabled] = useState(true);
  const [monthlyFreePsvLimit, setMonthlyFreePsvLimit] = useState(
    DEFAULT_MONTHLY_FREE_PSV_LIMIT,
  );
  const [editingUnit, setEditingUnit] = useState<BusinessUnit | null>(null);
  const [requireProviderReveal, setRequireProviderReveal] = useState(true);
  const [revealLimitEnabled, setRevealLimitEnabled] = useState(false);
  const [monthlyProviderRevealLimit, setMonthlyProviderRevealLimit] =
    useState(1);
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { data: organization } = useQuery(
    trpc.organization.getOrganization.queryOptions({
      orgId,
    }),
  );

  const { data: businessUnits, isLoading: isLoadingBusinessUnits } = useQuery(
    trpc.organization.getBusinessUnits.queryOptions({
      orgId,
    }),
  );

  const createUnitMutation = useMutation(
    trpc.organization.createBusinessUnit.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBusinessUnits.queryOptions({ orgId }),
        );
        setIsDialogOpen(false);
        setUnitName("");
        setEditingUnit(null);
      },
    }),
  );

  const updateUnitMutation = useMutation(
    trpc.organization.updateBusinessUnit.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getBusinessUnits.queryOptions({ orgId }),
        );
        setIsDialogOpen(false);
        setUnitName("");
        setEditingUnit(null);
      },
    }),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUnit) {
      updateUnitMutation.mutate({
        businessUnitId: editingUnit.id,
        name: unitName,
        autoUnpublishJobPostsDays: autoUnpublishEnabled
          ? autoUnpublishDays
          : null,
        requireProviderReveal,
        monthlyProviderRevealLimit: revealLimitEnabled
          ? monthlyProviderRevealLimit
          : null,
        enableHiringProcess: hiringProcessEnabled,
        enableDocumentSigning: documentSigningEnabled,
        enableProviderSearch: providerSearchEnabled,
        skipJobPostReview,
        useNewCredentialModel,
        enablePrimarySourceVerification: primarySourceVerificationEnabled,
        monthlyFreePrimarySourceVerificationLimit: freePsvLimitEnabled
          ? monthlyFreePsvLimit
          : null,
      });
    } else {
      createUnitMutation.mutate({
        orgId,
        name: unitName,
      });
    }
  };

  const handleEdit = (unit: BusinessUnit) => {
    setEditingUnit(unit);
    setUnitName(unit.name);
    setAutoUnpublishEnabled(unit.auto_unpublish_job_posts_days !== null);
    setAutoUnpublishDays(unit.auto_unpublish_job_posts_days ?? 30);
    setRequireProviderReveal(unit.require_provider_reveal);
    setRevealLimitEnabled(unit.monthly_provider_reveal_limit !== null);
    setMonthlyProviderRevealLimit(unit.monthly_provider_reveal_limit ?? 1);
    setHiringProcessEnabled(unit.enable_hiring_process);
    setDocumentSigningEnabled(unit.enable_document_signing);
    setProviderSearchEnabled(unit.enable_provider_search);
    setSkipJobPostReview(unit.skip_job_post_review);
    setUseNewCredentialModel(unit.use_new_credential_model);
    setPrimarySourceVerificationEnabled(
      unit.enable_primary_source_verification,
    );
    setFreePsvLimitEnabled(
      unit.monthly_free_primary_source_verification_limit !== null,
    );
    setMonthlyFreePsvLimit(
      unit.monthly_free_primary_source_verification_limit ??
        DEFAULT_MONTHLY_FREE_PSV_LIMIT,
    );
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingUnit(null);
    setUnitName("");
    setAutoUnpublishEnabled(true);
    setAutoUnpublishDays(30);
    setRequireProviderReveal(true);
    setRevealLimitEnabled(false);
    setMonthlyProviderRevealLimit(1);
    setHiringProcessEnabled(false);
    setDocumentSigningEnabled(false);
    setProviderSearchEnabled(false);
    setSkipJobPostReview(false);
    setUseNewCredentialModel(true);
    setPrimarySourceVerificationEnabled(false);
    setFreePsvLimitEnabled(true);
    setMonthlyFreePsvLimit(DEFAULT_MONTHLY_FREE_PSV_LIMIT);
    setIsDialogOpen(true);
  };

  return (
    <div className="min-h-full min-w-0 bg-slate-50">
      <div className="mx-auto max-w-7xl space-y-6 px-3 py-4 sm:px-4 sm:py-8 lg:px-8">
        <div className="space-y-4">
          <Button
            asChild
            variant="outline"
            className="w-full whitespace-normal sm:w-auto"
          >
            <Link to="/admin/organizations">← Back to Organizations</Link>
          </Button>
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="min-w-0 text-3xl font-semibold leading-tight text-slate-900 [overflow-wrap:anywhere] sm:text-4xl">
              Business Units - {organization?.name}
            </h1>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={handleAddNew}
                  className="w-full shrink-0 sm:w-auto"
                >
                  Add Business Unit
                </Button>
              </DialogTrigger>
              <DialogContent className="!w-[calc(100vw-2rem)] !max-w-2xl !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
                <DialogHeader className="pr-6 text-left">
                  <DialogTitle className="text-xl leading-7">
                    {editingUnit ? "Edit" : "Add New"} Business Unit
                  </DialogTitle>
                  <DialogDescription className="text-sm leading-6 text-slate-600">
                    {editingUnit
                      ? "Manage this business unit's name and settings."
                      : "Add a business unit to this organization."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="min-w-0 space-y-5">
                  <div className="grid min-w-0 gap-2">
                    <Label htmlFor="name">Business Unit Name</Label>
                    <Input
                      id="name"
                      value={unitName}
                      onChange={(e) => setUnitName(e.target.value)}
                      placeholder="Enter business unit name"
                    />
                  </div>
                  {editingUnit && (
                    <div className="min-w-0 space-y-3">
                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                        <div className="flex min-w-0 items-center justify-between gap-3">
                          <SettingLabel
                            htmlFor="autoUnpublish"
                            description="Automatically closes published job posts after the selected number of days since they were last published."
                          >
                            Auto-unpublish job posts
                          </SettingLabel>
                          <Switch
                            id="autoUnpublish"
                            checked={autoUnpublishEnabled}
                            onCheckedChange={setAutoUnpublishEnabled}
                            className="shrink-0"
                          />
                        </div>
                        {autoUnpublishEnabled && (
                          <div className="grid min-w-0 gap-2 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(0,1fr)_8rem] sm:items-center">
                            <Label htmlFor="autoUnpublishDays">
                              Unpublish after (days)
                            </Label>
                            <Input
                              id="autoUnpublishDays"
                              type="number"
                              min={1}
                              value={autoUnpublishDays}
                              onChange={(e) =>
                                setAutoUnpublishDays(
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                              className="min-w-0 w-full sm:text-right"
                            />
                          </div>
                        )}
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="requireProviderReveal"
                          description="Hides provider identity and protected profile details until the provider is explicitly revealed for this business unit."
                        >
                          Require provider reveal
                        </SettingLabel>
                        <Switch
                          id="requireProviderReveal"
                          checked={requireProviderReveal}
                          onCheckedChange={setRequireProviderReveal}
                          className="shrink-0"
                        />
                      </div>
                      <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                        <div className="flex min-w-0 items-center justify-between gap-3">
                          <SettingLabel
                            htmlFor="revealLimit"
                            description="Caps provider reveals for this business unit each calendar month. Leave off for unlimited reveals."
                          >
                            Monthly provider reveal limit
                          </SettingLabel>
                          <Switch
                            id="revealLimit"
                            checked={revealLimitEnabled}
                            onCheckedChange={setRevealLimitEnabled}
                            className="shrink-0"
                          />
                        </div>
                        {revealLimitEnabled && (
                          <div className="grid min-w-0 gap-2 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(0,1fr)_8rem] sm:items-center">
                            <Label htmlFor="monthlyProviderRevealLimit">
                              Maximum reveals per month
                            </Label>
                            <Input
                              id="monthlyProviderRevealLimit"
                              type="number"
                              min={1}
                              value={monthlyProviderRevealLimit}
                              onChange={(e) =>
                                setMonthlyProviderRevealLimit(
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                              className="min-w-0 w-full sm:text-right"
                            />
                          </div>
                        )}
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="hiringProcess"
                          description="Enables hiring process templates, candidate workflow tracking, and organization forms for this business unit."
                        >
                          Hiring Process & Forms
                        </SettingLabel>
                        <Switch
                          id="hiringProcess"
                          checked={hiringProcessEnabled}
                          onCheckedChange={setHiringProcessEnabled}
                          className="shrink-0"
                        />
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="documentSigning"
                          description="Each document signed costs $0.20. This allows PDF form templates to be added to hiring workflows and completed with electronic signatures."
                        >
                          Document signing
                        </SettingLabel>
                        <Switch
                          id="documentSigning"
                          checked={documentSigningEnabled}
                          onCheckedChange={setDocumentSigningEnabled}
                          className="shrink-0"
                        />
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="providerSearch"
                          description="Only enable this for internal Saile organizations. It allows the organization to find and message anyone on the platform."
                        >
                          Provider Search
                        </SettingLabel>
                        <Switch
                          id="providerSearch"
                          checked={providerSearchEnabled}
                          onCheckedChange={setProviderSearchEnabled}
                          className="shrink-0"
                        />
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="skipJobPostReview"
                          description="Job posts published by this business unit go live to providers immediately without Saile review"
                        >
                          Skip Job Post Review
                        </SettingLabel>
                        <Switch
                          id="skipJobPostReview"
                          checked={skipJobPostReview}
                          onCheckedChange={setSkipJobPostReview}
                          className="shrink-0"
                        />
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="useNewCredentialModel"
                          description="Reads this business unit's credential dashboard from the new credential model. Backfill the business unit's providers before enabling this setting."
                        >
                          New Credentials
                        </SettingLabel>
                        <Switch
                          id="useNewCredentialModel"
                          checked={useNewCredentialModel}
                          onCheckedChange={setUseNewCredentialModel}
                          className="shrink-0"
                        />
                      </div>
                      <div className={FEATURE_SETTING_ROW_CLASS_NAME}>
                        <SettingLabel
                          htmlFor="enablePrimarySourceVerification"
                          description="Lets this business unit run primary source verification checks (state licenses, registries, exclusion lists) and see the results on provider credentials. Checks call third-party sources, so enable per business unit."
                        >
                          Primary Source Verification
                        </SettingLabel>
                        <Switch
                          id="enablePrimarySourceVerification"
                          checked={primarySourceVerificationEnabled}
                          onCheckedChange={setPrimarySourceVerificationEnabled}
                          className="shrink-0"
                        />
                      </div>
                      {primarySourceVerificationEnabled && (
                        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                          <div className="flex min-w-0 items-center justify-between gap-3">
                            <SettingLabel
                              htmlFor="freePsvLimit"
                              description="Caps the verification runs this business unit can start each calendar month without an active billing plan. Units with an active billing plan are not capped. Turn off for unlimited free runs."
                            >
                              Monthly free verification limit
                            </SettingLabel>
                            <Switch
                              id="freePsvLimit"
                              checked={freePsvLimitEnabled}
                              onCheckedChange={setFreePsvLimitEnabled}
                              className="shrink-0"
                            />
                          </div>
                          {freePsvLimitEnabled && (
                            <div className="grid min-w-0 gap-2 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(0,1fr)_8rem] sm:items-center">
                              <Label htmlFor="monthlyFreePsvLimit">
                                Free verifications per month
                              </Label>
                              <Input
                                id="monthlyFreePsvLimit"
                                type="number"
                                min={0}
                                value={monthlyFreePsvLimit}
                                onChange={(e) => {
                                  const parsed = parseInt(e.target.value, 10);
                                  setMonthlyFreePsvLimit(
                                    Number.isNaN(parsed) ? 0 : parsed,
                                  );
                                }}
                                className="min-w-0 w-full sm:text-right"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="w-full sm:w-auto"
                      disabled={
                        createUnitMutation.isPending ||
                        updateUnitMutation.isPending
                      }
                    >
                      {createUnitMutation.isPending ||
                      updateUnitMutation.isPending
                        ? "Saving..."
                        : editingUnit
                          ? "Save"
                          : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {isLoadingBusinessUnits ? (
          <Card className="min-w-0 overflow-hidden rounded-2xl border-slate-200 bg-white p-0 shadow-xs">
            <div className="overflow-x-auto">
              <Table className="min-w-[640px] text-sm [&_th]:px-4 [&_td]:px-4 [&_td]:py-4">
                <TableHeader className="bg-slate-50 [&_th]:text-slate-600">
                  <TableRow className="border-slate-200 hover:bg-slate-50">
                    <TableHead>Name</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="sticky right-0 z-20 bg-slate-50 text-right shadow-[-1px_0_0_0_var(--color-slate-200)]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(3)].map((_, index) => (
                    <TableRow
                      key={index}
                      className="border-slate-200 hover:bg-slate-50/70"
                    >
                      <TableCell>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell className="sticky right-0 z-10 bg-white text-right shadow-[-1px_0_0_0_var(--color-slate-200)]">
                        <Skeleton className="ml-auto size-12 rounded-xl sm:w-28" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : !businessUnits || businessUnits.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-12 text-slate-600 shadow-xs">
            <svg
              className="mb-4 size-16 rounded-2xl bg-primary/5 p-3 text-primary"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              No Business Units Yet
            </h3>
            <p className="text-sm text-center mb-4">
              Get started by creating your first business unit
            </p>
            <Button onClick={handleAddNew}>Add Business Unit</Button>
          </div>
        ) : (
          <Card className="min-w-0 overflow-hidden rounded-2xl border-slate-200 bg-white p-0 shadow-xs">
            <div
              className="max-w-full overflow-x-auto focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              role="region"
              aria-label="Business units"
              tabIndex={0}
            >
              <Table className="min-w-[1100px] text-sm [&_th]:px-4 [&_td]:px-4 [&_td]:py-4">
                <TableHeader className="bg-slate-50 [&_th]:text-slate-600">
                  <TableRow className="border-slate-200 hover:bg-slate-50">
                    <TableHead className="min-w-48">Name</TableHead>
                    <TableHead>Auto-unpublish</TableHead>
                    <TableHead>Provider Reveal</TableHead>
                    <TableHead>Reveal Limit</TableHead>
                    <TableHead className="min-w-52">Features</TableHead>
                    <TableHead>Billing plan</TableHead>
                    <TableHead className="whitespace-nowrap">
                      Created At
                    </TableHead>
                    <TableHead className="sticky right-0 z-20 bg-slate-50 text-right shadow-[-1px_0_0_0_var(--color-slate-200)]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businessUnits.map((unit) => {
                    const enabledFlags = FEATURE_FLAGS.filter(
                      (flag) => unit[flag.key],
                    );
                    return (
                      <TableRow
                        key={unit.id}
                        className="group border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        <TableCell className="max-w-64 font-medium text-slate-900 [overflow-wrap:anywhere]">
                          {unit.name}
                        </TableCell>
                        <TableCell>
                          {unit.auto_unpublish_job_posts_days !== null ? (
                            <span className="text-emerald-700">
                              {unit.auto_unpublish_job_posts_days} days
                            </span>
                          ) : (
                            <span className="text-slate-500">Off</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {unit.require_provider_reveal ? (
                            <span className="text-emerald-700">Required</span>
                          ) : (
                            <span className="text-slate-500">Off</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {unit.monthly_provider_reveal_limit !== null ? (
                            <span className="text-emerald-700">
                              {unit.monthly_provider_reveal_limit}
                            </span>
                          ) : (
                            <span className="text-slate-500">Unlimited</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-64">
                          {enabledFlags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {enabledFlags.map((flag) => (
                                <Badge
                                  key={flag.key}
                                  variant="outline"
                                  size="small"
                                  className="whitespace-normal border-primary/15 bg-primary/5 text-primary"
                                >
                                  {flag.label}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <BillingPlanDialog
                            organizationBusinessUnitId={unit.id}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-slate-500">
                          {new Date(unit.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="sticky right-0 z-10 bg-white text-right shadow-[-1px_0_0_0_var(--color-slate-200)] group-hover:bg-slate-50">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-12 gap-2 px-0 sm:w-auto sm:px-5"
                                aria-label={`Actions for ${unit.name}`}
                              >
                                <span className="hidden sm:inline">
                                  Actions
                                </span>
                                <MoreHorizontal
                                  aria-hidden="true"
                                  className="size-5 text-slate-500 sm:hidden"
                                />
                                <ChevronDown
                                  aria-hidden="true"
                                  className="hidden size-4 text-slate-500 sm:block"
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="min-w-44 rounded-xl border-slate-200 bg-white p-1.5 text-slate-700 shadow-lg"
                            >
                              <DropdownMenuItem
                                onClick={() => handleEdit(unit)}
                                className="min-h-12 cursor-pointer gap-2 rounded-lg focus:bg-primary/5 focus:text-primary"
                              >
                                <Pencil
                                  aria-hidden="true"
                                  className="size-4 shrink-0"
                                />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                asChild
                                className="min-h-12 cursor-pointer gap-2 rounded-lg focus:bg-primary/5 focus:text-primary"
                              >
                                <Link
                                  to="/admin/organizations/$orgId/business-units/$businessUnitId/users"
                                  params={{ orgId, businessUnitId: unit.id }}
                                >
                                  <Users
                                    aria-hidden="true"
                                    className="size-4 shrink-0"
                                  />
                                  View Users
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  "/_admin/admin/organizations/$orgId/business-units/",
)({
  component: BusinessUnitsPage,
});
